import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, OnInit, Signal, TemplateRef, viewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { syncQueryParam } from 'src/app/helpers/sync-query-param.helper';
import { UiSearchComponent } from 'src/app/shared/ui-kit/ui-search/ui-search.component';
import { AppState } from '../../../../store/app.state';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { RouteModalService, UiButtonComponent } from '@ui';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createShelfsTableConfig, ShelfCellContext } from './shelfs-table.util';
import { MenuActions } from '@states/menu/menu.action-types';
import { ShelfsActions } from '@states/shelfs/shelfs.action-types';
import { MenuKey } from '@enums/menu.enum';
import { ShelfsSelectors } from '@states/shelfs/shelfs.selector-types';
import { ShelfsModel } from '@models/shelfs.model';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import ZoneSelectionType = CameraPickerModels.ZoneSelectionType;
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { Dictionary } from '@ngrx/entity/src';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from 'src/app/shared/ui-kit/ui-preloader/ui-preloader.component';
import { NgFor, NgIf } from '@angular/common';
import { EdgeCamera } from 'src/app/cameras/camera.model';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UiCameraSelectButtonComponent } from '@ui';
import { cloneDeep } from 'lodash';
import { SelectedCamera } from '@models/alert-events.model';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TagFilterComponent } from '../tags/components/tag-filter/tag-filter.component';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { UiCameraSnapshotComponent } from 'src/app/shared/ui-kit/ui-camera-snapshot/ui-camera-snapshot.component';
import { TagMenuComponent } from '../tags/components/tag-menu/tag-menu.component';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, UiFilterChipComponent } from '@ui';
import { ConfirmModalModel } from 'src/app/services/confirm-dialogs/confirm-modal.model';
import { UiZoneSelectorDialogComponent, UiZoneSelectorDialogData, UiZoneSelectorDialogResult } from 'src/app/shared/ui-kit/ui-zone-selector/ui-zone-selector-dialog/ui-zone-selector-dialog.component';
import { select } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { SharedEffects } from '@effects/shared.effects';
import { ofType } from '@ngrx/effects';
import { generateCameraIdsIncludesExcludes } from 'src/app/helpers/common.helpers';

@Component({
  selector: 'app-shelfs',
  imports: [
    TranslatePipe,
    UiSearchComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiCameraSelectButtonComponent,
    NgIf,
    MatMenu,
    UiFilterChipComponent,
    MatMenuTrigger,
    NgFor,
    MatTooltip,
    TagFilterComponent,
    ReactiveFormsModule,
    UiCameraSnapshotComponent,
    TagMenuComponent,
    UiButtonComponent,
  ],
  templateUrl: './shelfs.component.html',
  styleUrl: './shelfs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShelfsComponent implements OnInit {
  protected readonly Object = Object;

  public loaderColor = PreloaderColor;
  public noResults = features.shelfs.noResults;
  public noDataObj = features.shelfs.noData;

  public cameraIdsFilter: FormControl<Dictionary<SelectedCamera>> = new FormControl<Dictionary<SelectedCamera>>({});
  public tagIdsFilter: FormControl<string[]> = new FormControl<string[]>([]);

  protected search = syncQueryParam<string>('name');
  protected cameraIdsIncludesQueryParam = syncQueryParam<string>('cameraIdsIncludes');
  protected cameraIdsExcludesQueryParam = syncQueryParam<string>('cameraIdsExcludes');
  protected tagsQueryParam = syncQueryParam<string>('tagIds');

  protected readonly nameCellTemplate = viewChild<TemplateRef<ShelfCellContext>>('nameCell');
  protected readonly tagsCellTemplate = viewChild<TemplateRef<ShelfCellContext>>('tagsCell');

  public selectIsLoadingSig: Signal<boolean> = this.store$.selectSignal(ShelfsSelectors.selectIsLoading);
  public selectIsLoadedSig: Signal<boolean> = this.store$.selectSignal(ShelfsSelectors.selectIsLoaded);
  public selectShelfsSig: Signal<ShelfsModel.ShelfDocument[]> = this.store$.selectSignal(ShelfsSelectors.selectShelfs);
  public selectIsSavingSig: Signal<boolean> = this.store$.selectSignal(ShelfsSelectors.selectIsSaving);
  public selectCamerasSig: Signal<Dictionary<EdgeCamera.CameraItem>> = this.store$.selectSignal(CameraSelectors.selectCamerasLookup);
  public selectDatabaseTagsEntitiesSig: Signal<Dictionary<DatabaseTagsModel.DatabaseTagDocument>> = this.store$.selectSignal(DatabaseTagsSelectors.selectDatabaseTagEntities);

  public readonly tableConfig = computed(() => {
    const nameTemplate = this.nameCellTemplate();
    const tagsTemplate = this.tagsCellTemplate();

    if (!nameTemplate || !tagsTemplate) {
      return null;
    }

    return createShelfsTableConfig({
      nameCellTemplate: nameTemplate,
      tagsCellTemplate: tagsTemplate,
      camerasLookup: this.selectCamerasSig,
    });
  });

  constructor(
    private store$: Store<AppState>,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private modalService: RouteModalService,
    private cdr: ChangeDetectorRef,
    private sharedEffects$: SharedEffects,
  ) {
    effect(() => {
      const search = this.search();
      if (search !== undefined) {
        this.store$.dispatch(ShelfsActions.setFilter({ field: 'name', value: search }));
      }

      const cameraIdsIncludes = this.cameraIdsIncludesQueryParam();
      if (cameraIdsIncludes !== undefined) {
        this.store$.dispatch(ShelfsActions.setFilter({ field: 'cameraIdsIncludes', value: cameraIdsIncludes }))
      }

      const cameraIdsExcludes = this.cameraIdsExcludesQueryParam();
      if (cameraIdsExcludes !== undefined) {
        this.store$.dispatch(ShelfsActions.setFilter({ field: 'cameraIdsExcludes', value: cameraIdsExcludes }))
      }

      const tagIds = this.tagsQueryParam();
      if (tagIds !== undefined) {
        this.store$.dispatch(ShelfsActions.setFilter({ field: 'tagIds', value: tagIds }))
      }
    })
  }

  ngOnInit(): void {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.shelfs }));

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) =>
            x.params['type'] === ConfirmModalModel.Action.removeShelf &&
            !!x?.params['id'],
        ),
      )
      .subscribe((res) => {
        if (res.params['id']) {
          this.store$.dispatch(
            ShelfsActions.deleteShelf({ id: res.params['id'] }),
          );
        }
      });

    this.cameraIdsFilter.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedCameras) => {
        const cameraLookup = this.selectCamerasSig();
        const allowedCameraIds = Object.keys(cameraLookup);

        const { cameraIdsIncludes, cameraIdsExcludes } = generateCameraIdsIncludesExcludes(selectedCameras, allowedCameraIds);

        this.cameraIdsIncludesQueryParam.set(cameraIdsIncludes.join(','));
        this.cameraIdsExcludesQueryParam.set(cameraIdsExcludes.join(','));
      });

    this.tagIdsFilter.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tags) => {
        this.tagsQueryParam.set(tags.join(','));
      });

    const qp = this.route.snapshot.queryParamMap;

    const qpCameraIdsIncludes = (qp.get('cameraIdsIncludes') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const qpCameraIdsExcludes = (qp.get('cameraIdsExcludes') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const cameraIds = this.resolveCameraIds(qpCameraIdsIncludes, qpCameraIdsExcludes);

    const qpTags = (qp.get('tagIds') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (cameraIds?.length) {
      const cameraLookup = this.selectCamerasSig();

      if (!cameraLookup || Object.keys(cameraLookup).length === 0) return;

      const filtered: Dictionary<EdgeCamera.CameraItem> = {};

      for (const id of cameraIds) {
        const cam = cameraLookup[id];
        if (cam) {
          filtered[id] = cam;
        }
      }

      const selectedDict = this.cameraDictToSelectedCameraDict(filtered);

      this.cameraIdsFilter.setValue(selectedDict, { emitEvent: false });
    }

    if (qpTags?.length) {
      this.tagIdsFilter.setValue(qpTags);
    }

    if (!qpTags?.length && !cameraIds?.length) {
      this.store$.dispatch(ShelfsActions.resetToInitialState());
      this.store$.dispatch(ShelfsActions.startLoadingShelfs());
    } else {
      this.store$.dispatch(ShelfsActions.setIsLoading({ isLoading: true }));
    }

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createShelf,
          open: () => this.openAddShelfDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public cleanAllFilters() {
    this.cameraIdsFilter.setValue({});
    this.tagIdsFilter.setValue([]),
      this.search.set(null);
  }

  public cleanTagsFilterControl(tag: string) {
    const current = this.tagIdsFilter.value ?? [];
    const updated = current.filter((v) => v !== tag);
    this.tagIdsFilter.patchValue(updated);
  }

  public create() {
    this.modalService.openModal(openDialogByParams.createShelf);
  }

  public openAddShelfDialog() {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: false,
        zoneSelection: true,
        zoneSelectionType: ZoneSelectionType.ZONES,
        cameraLookupEnabled: true,
        selectedValueInputType: CameraPickerModels.SelectedCameraInputType.CameraLookup,
        validateZonesEnabled: true,
        isNameVisible: true,
        isTagsVisible: true,
      },
    };

    const dialogRef = this.dialog.open(CameraPickerModalComponent, {
      width: '600px',
      panelClass: 'modal-no-padding',
      height: '70vh',
      disableClose: true,
      data,
    });

    dialogRef.afterClosed().subscribe((selectedCameras: Dictionary<CameraPickerModels.SelectedCamera>) => {
      if (!selectedCameras) {
        return;
      }
      const cameras = Object.values(selectedCameras);
      if (cameras?.length) {
        const camera = cameras[0];
        const shelfDto = {
          name: camera.name || this.translateService.instant('SHELFS.UNTITLED_SHELF'),
          location: {
            cameraId: camera.cameraId,
            locationId: camera.locationId,
            edgeId: camera.edgeId,
            zones: camera.cameraLookup.zones,
            markedIdx: camera.cameraLookup.markedIdx,
            exclude: camera.cameraLookup.zonesExclude,
          },
          tags: camera.tagIds
        }

        this.store$.dispatch(ShelfsActions.createShelf({ dto: shelfDto }));
      }
    });

    return dialogRef;
  }

  public selectCamera(): void {
    const cams = cloneDeep(this.cameraIdsFilter.value);

    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: true,
      },
      selectedValue: cams,
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((selectedCameras: Dictionary<SelectedCamera>) => {
        if (selectedCameras) {
          this.cameraIdsFilter.setValue(selectedCameras);
          this.cdr.detectChanges();
        }
      });
  }

  public onRemoveCamera(cameraId: string): void {
    const current = { ...this.cameraIdsFilter.value };

    if (current[cameraId]) {
      delete current[cameraId];
      this.cameraIdsFilter.setValue(current);
    }
  }

  public onRowAction(event: {
    actionId: string;
    row: ShelfsModel.ShelfDocument;
  }): void {
    if (event.actionId === 'edit') {
      this.edit(event.row.id, event.row.name, event.row.tags);
    } else if (event.actionId === 'delete') {
      this.delete(event.row.id, event.row.name);
    }
  }

  private edit(id: string, name: string, tags: string[]): void {
    this.store$.dispatch(ShelfsActions.reseSelectedShelf());
    this.store$.dispatch(ShelfsActions.startLoadingShelfDetailsById({ id }));

    this.store$.pipe(
      select(ShelfsSelectors.selectSelectedShelf),
      filter((shelf) => !!shelf),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((shelf) => {
      const data: UiZoneSelectorDialogData = {
        camera: {
          locationId: shelf.location.locationId,
          edgeId: shelf.location.edgeId,
          cameraId: shelf.location.cameraId,
          edgeOnly: {
            cameraId: shelf.location.cameraId,
          }
        },
        zones: shelf.location?.zones,
        markedIdx: shelf.location?.markedIdx,
        exclude: shelf.location?.exclude,
        isNameVisible: true,
        name: name,
        isTagsVisible: true,
        tagIds: tags
      };

      this.dialog
        .open(UiZoneSelectorDialogComponent, {
          data,
          panelClass: 'modal-no-padding',
          maxWidth: '115vh',
          height: '80vh',
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: UiZoneSelectorDialogResult) => {
          if (result && typeof result === 'object') {
            const dto = {
              id: id,
              name: result.name,
              location: {
                zones: result.zones,
                markedIdx: result.markedIdx,
              },
              tags: result.tagIds,
            } as Partial<ShelfsModel.ShelfCreateDto>;

            this.store$.dispatch(ShelfsActions.editShelf({ id: id, dto: dto }));
          }
        });
    });
  }

  private delete(id: string, name: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant('SHELFS.DIALOGS.DELETE.TITLE'),
          msg: this.translateService.instant('SHELFS.DIALOGS.DELETE.MESSAGE', { name: `"${name}"` }),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
          params: {
            type: ConfirmModalModel.Action.removeShelf,
            id: id,
          },
        },
      }),
    );
  }

  public onLoadMore(): void {
    this.store$.dispatch(ShelfsActions.getShelfs());
  }

  private cameraItemtoSelectedCamera(cam: EdgeCamera.CameraItem | undefined | null): SelectedCamera | null {
    if (!cam) return null;

    if (!cam.edgeOnly?.cameraId) return null;

    if (!cam.edgeId) return null;

    return {
      cameraId: cam.edgeOnly?.cameraId,
      locationId: cam.locationId,
      edgeId: cam.edgeId,
    };
  }

  private cameraDictToSelectedCameraDict(
    dict: Dictionary<EdgeCamera.CameraItem>
  ): Dictionary<SelectedCamera> {
    const result: Dictionary<SelectedCamera> = {};

    for (const id of Object.keys(dict)) {
      const cam = dict[id];
      const selected = this.cameraItemtoSelectedCamera(cam);

      if (selected) {
        result[id] = selected;
      }
    }

    return result;
  }

  private resolveCameraIds(
    cameraIdsIncludes?: string[] | null,
    cameraIdsExcludes?: string[] | null,
  ): string[] {
    const cameraLookup = this.selectCamerasSig();
    const allowedCameraIds = Object.keys(cameraLookup);

    if (cameraIdsIncludes?.length) {
      return cameraIdsIncludes;
    }

    if (cameraIdsExcludes?.length) {
      return allowedCameraIds.filter((id) => !cameraIdsExcludes.includes(id));
    }

    return [];
  }
}

